import { api } from '@/lib/trpc/react';
import { useRouter } from 'next/router';

const useDeleteChatbot = ({ onMutate }: { onMutate?: () => void } = {}) => {
  const utils = api.useUtils();
  const router = useRouter();

  const { mutate: mutateDelete, isPending: isDeleting } =
    api.chatbot.newDelete.useMutation({
      onMutate: async (vars) => {
        onMutate?.();

        const ids = vars.map((v) => v.id);

        const prevData = utils.workspace.all.getData();
        if (ids.includes(router.query.id as string)) router.push('/new');

        utils.workspace.all.setData(undefined, (prev) => {
          if (!prev) return prev;
          const newParentContextForChildren = new Map<string, string | null>();

          // Track deleted chat IDs for updating parent entities
          const deletedChatIds = new Set<string>();

          for (const entity of prev.entities) {
            if (ids.includes(entity.id) && entity.type === 'GROUP') {
              newParentContextForChildren.set(entity.id, entity.groupId);
            }
            // Check if the entity is a chat being deleted
            if (ids.includes(entity.id) && entity.type === 'CHAT') {
              deletedChatIds.add(entity.id);
            }
            // Also check if any of the entity's chats are being deleted
            if (entity.chats && entity.chats.length > 0) {
              for (const chat of entity.chats) {
                if (ids.includes(chat.id)) {
                  deletedChatIds.add(chat.id);
                }
              }
            }
          }

          const updatedEntities = prev.entities
            .map((entity) => {
              if (
                entity.groupId &&
                newParentContextForChildren.has(entity.groupId) &&
                !ids.includes(entity.id)
              ) {
                const newGroupIdForChild =
                  newParentContextForChildren.get(entity.groupId) ?? null;
                return {
                  ...entity,
                  groupId: newGroupIdForChild,
                };
              }

              // Update entity's chats array to remove deleted chats
              if (
                entity.chats &&
                entity.chats.length > 0 &&
                deletedChatIds.size > 0
              ) {
                const filteredChats = entity.chats.filter(
                  (chat) => !deletedChatIds.has(chat.id),
                );
                // Only return updated entity if chats were actually removed
                if (filteredChats.length !== entity.chats.length) {
                  return {
                    ...entity,
                    chats: filteredChats,
                  };
                }
              }

              return entity;
            })
            .filter((entity) => !ids.includes(entity.id));

          return {
            ...prev,
            entities: updatedEntities,
          };
        });

        return { prevData };
      },

      onError(_err, _, ctx) {
        utils.workspace.all.setData(undefined, ctx?.prevData);
      },
    });

  return { mutateDelete, isDeleting };
};

export default useDeleteChatbot;
